"use client"

import { useTranslation } from "react-i18next"
import { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import {
  RefreshCw,
  Sparkles,
  ShieldOff,
  Mic,
  CheckCircle,
  ArrowRight,
} from "lucide-react"
import { useRouter } from "next/navigation"
import { handleSwissCTAClick, handleDefaultCTAClick } from "../utils/cta"
import { trackEvent } from "../lib/analytics"

const benefits = [
  {
    icon: <RefreshCw className="w-6 h-6" />,
    titleKey: "benefit1.title",
    descKey: "benefit1.desc",
    highlightKey: "benefit1.highlight",
    features: ["feature1_1", "feature1_2", "feature1_3"],
  },
  {
    icon: <Sparkles className="w-6 h-6" />,
    titleKey: "benefit2.title",
    descKey: "benefit2.desc",
    highlightKey: "benefit2.highlight",
    features: ["feature2_1", "feature2_2", "feature2_3"],
  },
  {
    icon: <ShieldOff className="w-6 h-6" />,
    titleKey: "benefit3.title",
    descKey: "benefit3.desc",
    highlightKey: "benefit3.highlight",
    features: ["feature3_1", "feature3_2", "feature3_3"],
  },
  {
    icon: <Mic className="w-6 h-6" />,
    titleKey: "benefit4.title",
    descKey: "benefit4.desc",
    highlightKey: "benefit4.highlight",
    features: ["feature4_1", "feature4_2", "feature4_3"],
  },
]

const BenefitsSection = ({ isSwiss = false }) => {
  const [mounted, setMounted] = useState(false)
  const { t } = useTranslation("Benefits")
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleCTAClick = useCallback(() => {
    trackEvent("cta_click", { location: "benefits" })
    if (isSwiss) {
      handleSwissCTAClick(router)
    } else {
      handleDefaultCTAClick(router)
    }
  }, [isSwiss, router])

  if (!mounted) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((_, idx) => (
              <div key={idx} className="bg-slate-50 rounded-xl p-6 h-64 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300"
            >
              {/* Icon */}
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                {benefit.icon}
              </div>

              {/* Badge */}
              <span className="inline-block text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full mb-3">
                {t(benefit.highlightKey)}
              </span>

              {/* Title */}
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t(benefit.titleKey)}
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                {t(benefit.descKey)}
              </p>

              {/* Feature list */}
              <ul className="space-y-1.5">
                {benefit.features.map((feature, featureIdx) => (
                  <li key={featureIdx} className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <span className="text-xs text-slate-600">{t(feature)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <button
            onClick={handleCTAClick}
            className="group bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-full transition duration-200 hover:shadow-lg hover:shadow-blue-600/25"
          >
            <span className="flex items-center gap-2">
              {t(isSwiss ? "ctaButtonSwiss" : "ctaButton")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </button>
          <p className="text-slate-500 text-sm mt-3">
            {t("ctaSubtext")}
          </p>
        </div>
      </div>
    </section>
  )
}

export default BenefitsSection
